import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  Users,
  UserRound,
  Warehouse,
  Package,
  LogOut,
  X,
  ClipboardList,
  Repeat,
  IndianRupee,
  Truck,
  Mail,
  Settings,
  History,
  ImagePlus,
  Video,
  Megaphone,
  FileBarChart2,
  Boxes,
  Smartphone,
  BellRing,
  ChevronUp,
  Images,
  Tag,
  CreditCard,
  PanelLeftClose,
  PanelLeftOpen,
  Zap,
  MessageCircleMore,
} from "lucide-react";

import logoIcon from "../../../assets/logo.png";
import wholesumWordmark from "../../../assets/wholesum.svg";
import dashboardGroceryBag from "../../../assets/dashboard-grocery-bag.png";
import {
  clearAuthFlow,
  clearAuthSession,
  getAuthSession,
  getAuthUser,
  hasPermission,
} from "../../AdminAuth/components/authStore";
import DashboardSwitchModal from "./DashboardSwitchModal";
import { SupportService } from "../../../services/support.service";
import { getSupportSocket } from "../../../services/supportSocket";

const linkBase =
  "group relative flex h-11 w-full shrink-0 items-center gap-3 rounded-[11px] px-3 text-[14px] font-bold transition-all duration-200 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[3px] before:rounded-full before:opacity-0 after:ml-auto after:text-[18px] after:font-normal after:leading-none after:content-['›'] [&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:shrink-0 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left";

function formatRole(role) {
  return String(role || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getDisplayName(user) {
  return (
    user?.name ||
    user?.fullName ||
    user?.adminName ||
    user?.username ||
    user?.email?.split("@")?.[0] ||
    "Admin"
  );
}

function SidebarContent({ onNavigate, onLogout, collapsed = false, onToggleCollapse = null, showCollapseControl = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const session = getAuthSession() || {};
  const user = getAuthUser() || {};

  const dashboardAccess = session?.dashboardAccess || {};
  const canSwitchDashboard = !!dashboardAccess?.canSwitchDashboard;

  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);

  const displayName = getDisplayName(user);
  const displayRole = formatRole(user?.role) || "Admin";

  const path = location.pathname;

  useEffect(() => {
    setActionsOpen(false);
  }, [path]);

  const isDashboard = path === "/admin/dashboard";
  const isEmployees =
    path.startsWith("/admin/dashboard/employees") ||
    path.startsWith("/admin/dashboard/roles");
  const isUsers = path.startsWith("/admin/dashboard/users");
  const isWarehouse = path.startsWith("/admin/dashboard/warehouse");
  const isProducts = path.startsWith("/admin/dashboard/products");
  const isPricing = path.startsWith("/admin/dashboard/pricing");
  const isInventory = path.startsWith("/admin/dashboard/inventory");
  const isStock = path.startsWith("/admin/dashboard/stock");
  const isDeliveryPartners = path.startsWith("/admin/dashboard/delivery-partners");
  const isDriverWallet = path.startsWith("/admin/dashboard/driver-wallet");
  const isPaymentCollections = path.startsWith("/admin/dashboard/payment-collections");
  const isEmailTemplates = path.startsWith("/admin/dashboard/email-templates");
  const isReports = path.startsWith("/admin/dashboard/reports") || path.startsWith("/admin/dashboard/order-analytics");
  const isSupport = path.startsWith("/admin/dashboard/support");
  const isSettings = path.startsWith("/admin/dashboard/settings");
  const isActivityLogs = path.startsWith("/admin/dashboard/activity-logs");
  const isPosters = path.startsWith("/admin/dashboard/posters");
  const isShortVideos = path.startsWith("/admin/dashboard/short-videos");
  const isBroadcastMessages = path.startsWith("/admin/dashboard/broadcast-messages");
  const isUserNotifications = path.startsWith("/admin/dashboard/user-notifications");
  const isAppLayout = path.startsWith("/admin/dashboard/app-layout");
  const isImageManager = path.startsWith("/admin/dashboard/image-manager"); // <-- add
  const isCoupons = path.startsWith("/admin/dashboard/coupons");

  const isSuperAdmin = String(user?.role || "").toLowerCase() === "super_admin";

  const normalizedRole = String(user?.role || "").toLowerCase();

  const canViewSupport =
    ["super_admin", "admin"].includes(normalizedRole) ||
    hasPermission("admin.support");

  useEffect(() => {
    if (!canViewSupport) return undefined;
    let active = true;
    const load = async () => {
      try {
        const res = await SupportService.getStats();
        if (active) setSupportUnread(Number(res?.data?.unreadConversations || 0));
      } catch {
        if (active) setSupportUnread(0);
      }
    };
    load();
    const timer = setInterval(load, 30000);
    const socket = getSupportSocket();
    const refresh = () => load();
    socket?.on("support:message-created", refresh);
    socket?.on("support:conversation-cleared", refresh);
    socket?.on("support:all-cleared", refresh);
    return () => {
      active = false;
      clearInterval(timer);
      socket?.off("support:message-created", refresh);
      socket?.off("support:conversation-cleared", refresh);
      socket?.off("support:all-cleared", refresh);
    };
  }, [canViewSupport, path]);

  const canViewEmployees =
    isSuperAdmin || hasPermission(["employees.view", "admin.employees.read"]);

  const canViewUsers = normalizedRole === "super_admin" || normalizedRole === "admin";

  const canViewWarehouses =
    isSuperAdmin || hasPermission(["warehouses.view", "admin.warehouses.read"]);

  const canViewProducts =
    isSuperAdmin ||
    hasPermission([
      "products.view",
      "admin.products.read",
      "admin.catalog.read",
      "admin.catalog.manage",
    ]);

  const canViewPricing = canViewProducts;

  const canViewInventory =
    isSuperAdmin ||
    hasPermission([
      "inventory.view",
      "admin.inventory.read",
      "warehouse.inventory.read",
    ]);

  const canViewDeliveryPartners =
    isSuperAdmin ||
    hasPermission([
      "delivery.partners.read",
      "admin.delivery_partners.read",
    ]);

  const canViewEmailTemplates =
    isSuperAdmin ||
    hasPermission([
      "admin.email_templates.read",
      "email.templates.read",
      "settings.email_templates.read",
    ]);

  const canViewActivityLogs = !!session;

  const canViewPosters =
    isSuperAdmin || hasPermission(["admin.posters.read", "admin.posters.manage"]);

  const canViewImageManager =
    isSuperAdmin || hasPermission(["admin.catalog.read", "admin.catalog.manage"]);

  const canViewShortVideos =
    isSuperAdmin || hasPermission(["admin.short_videos.read", "admin.short_videos.manage"]);

  const canViewBroadcastMessages =
    isSuperAdmin || hasPermission(["admin.broadcasts.read", "admin.broadcasts.manage"]);

  const canViewCoupons =
    isSuperAdmin || hasPermission(["admin.coupons.read", "admin.coupons.manage"]);

  const canViewDriverWallet =
    isSuperAdmin || hasPermission(["admin.driver_wallet.read", "admin.driver_wallet.manage"]);

  const canViewPaymentCollections =
    isSuperAdmin || hasPermission(["admin.payment_collections.read"]);

  const canViewAppLayout =
    isSuperAdmin || hasPermission(["admin.app_layout.read", "admin.app_layout.manage"]);


  const canViewReports =
    isSuperAdmin ||
    hasPermission(["admin.reports.read", "reports.read", "admin.analytics.read", "admin.orders.read"]);

  const canViewSettings =
    isSuperAdmin ||
    hasPermission([
      "admin.settings.read",
      "admin.settings.update",
    ]);

  const linkClass = (active) =>
    [
      linkBase,
      collapsed ? "justify-center px-0 after:hidden [&>span]:hidden" : "",
      active
        ? "bg-gradient-to-r from-[#ffe9ee] to-[#fff3f5] text-[#e34f68] shadow-[0_7px_18px_rgba(224,91,116,0.09)] before:bg-[#ff3f67] before:opacity-100 after:text-[#ed5870]"
        : "text-[#526783] after:text-[#8fa0b8] hover:bg-[#fff8f9] hover:text-[#d86a78] hover:after:translate-x-0.5 hover:after:text-[#d86a78]",
    ].filter(Boolean).join(" ");

  const go = (to) => {
    onNavigate?.();
    navigate(to);
  };

  const handleSwitched = () => {
    onNavigate?.();
    navigate("/admin/dashboard", { replace: true });
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-visible bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_72%,#fff8fa_100%)]">
      <div className={`shrink-0 ${collapsed ? "px-3 pt-3" : "px-4 pt-3"}`}>
        <div className={`flex h-[58px] items-center ${collapsed ? "justify-center" : "justify-between gap-3"}`}>
          <div className={`flex min-w-0 items-center ${collapsed ? "justify-center" : "gap-2.5"}`}>
            <img
              src={logoIcon}
              alt="WholeSum"
              className={`${collapsed ? "h-10 w-10" : "h-11 w-11"} shrink-0 select-none object-contain`}
              draggable={false}
            />
            {!collapsed ? (
              <img
                src={wholesumWordmark}
                alt="WholeSum"
                className="h-[30px] min-w-0 max-w-[118px] select-none object-contain"
                draggable={false}
              />
            ) : null}
          </div>

          {showCollapseControl && !collapsed ? (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-[12px] border border-slate-200/80 bg-white text-slate-500 shadow-[0_6px_16px_rgba(15,23,42,0.06)] transition hover:border-rose-200 hover:bg-rose-50 hover:text-[#d86a78]"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <PanelLeftClose size={17} />
            </button>
          ) : null}
        </div>

        <div className="mt-2 h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </div>

      {showCollapseControl && collapsed ? (
        <button
          type="button"
          onClick={onToggleCollapse}
          className="mx-auto mt-3 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[#f2c6cc] bg-[#fff6f7] text-[#d86a78] shadow-sm transition hover:bg-white"
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <PanelLeftOpen size={17} />
        </button>
      ) : null}

      <div className={`${collapsed ? "mt-2 px-2" : "mt-2 pl-3 pr-1.5"} min-h-0 flex-1 overflow-hidden`}>
        <nav className="sidebar-scroll flex h-full min-h-0 flex-col gap-0.5 overflow-y-auto overscroll-contain pr-1.5">
          <button
            type="button"
            className={linkClass(isDashboard)}
            onClick={() => go("/admin/dashboard")}
          >
            <LayoutGrid size={18} />
            <span>Dashboard</span>
          </button>

          {canViewEmployees ? (
            <button
              type="button"
              className={linkClass(isEmployees)}
              onClick={() => go("/admin/dashboard/employees")}
            >
              <Users size={18} />
              <span>Employee&apos;s</span>
            </button>
          ) : null}


          {canViewUsers ? (
            <button
              type="button"
              className={linkClass(isUsers)}
              onClick={() => go("/admin/dashboard/users")}
            >
              <UserRound size={18} />
              <span>Users</span>
            </button>
          ) : null}

          {canViewDeliveryPartners ? (
            <button
              type="button"
              className={linkClass(isDeliveryPartners)}
              onClick={() => go("/admin/dashboard/delivery-partners")}
            >
              <Truck size={18} />
              <span>Delivery Partners</span>
            </button>
          ) : null}

          {canViewDriverWallet ? (
            <button
              type="button"
              className={linkClass(isDriverWallet)}
              onClick={() => go("/admin/dashboard/driver-wallet")}
            >
              <IndianRupee size={18} />
              <span>Driver Wallet</span>
            </button>
          ) : null}

          {canViewPaymentCollections ? (
            <button
              type="button"
              className={linkClass(isPaymentCollections)}
              onClick={() => go("/admin/dashboard/payment-collections")}
            >
              <CreditCard size={18} />
              <span>Payment Collections</span>
            </button>
          ) : null}

          {canViewWarehouses ? (
            <button
              type="button"
              className={linkClass(isWarehouse)}
              onClick={() => go("/admin/dashboard/warehouse")}
            >
              <Warehouse size={18} />
              <span>Warehouse</span>
            </button>
          ) : null}

          {canViewProducts ? (
            <button
              type="button"
              className={linkClass(isProducts)}
              onClick={() => go("/admin/dashboard/products")}
            >
              <Package size={18} />
              <span>Products</span>
            </button>
          ) : null}

          {canViewAppLayout ? (
            <button
              type="button"
              className={linkClass(isAppLayout)}
              onClick={() => go("/admin/dashboard/app-layout")}
            >
              <Smartphone size={18} />
              <span>App Layout</span>
            </button>
          ) : null}

          {canViewPricing ? (
            <button
              type="button"
              className={linkClass(isPricing)}
              onClick={() => go("/admin/dashboard/pricing")}
            >
              <IndianRupee size={18} />
              <span>Pricing</span>
            </button>
          ) : null}

          {canViewInventory ? (
            <button
              type="button"
              className={linkClass(isInventory)}
              onClick={() => go("/admin/dashboard/inventory")}
            >
              <ClipboardList size={18} />
              <span>Inventory</span>
            </button>
          ) : null}

          {canViewInventory ? (
            <button
              type="button"
              className={linkClass(isStock)}
              onClick={() => go("/admin/dashboard/stock")}
            >
              <Boxes size={18} />
              <span>Stock</span>
            </button>
          ) : null}



          {canViewSupport ? (
            <button
              type="button"
              className={linkClass(isSupport)}
              onClick={() => go("/admin/dashboard/support")}
            >
              <MessageCircleMore size={18} />
              <span>Support</span>
              {!collapsed && supportUnread > 0 ? (
                <b className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-[#ee5670] px-1 text-[10px] font-black text-white">
                  {Math.min(99, supportUnread)}
                </b>
              ) : null}
            </button>
          ) : null}

          {canViewReports ? (
            <button
              type="button"
              className={linkClass(isReports)}
              onClick={() => go("/admin/dashboard/reports")}
            >
              <FileBarChart2 size={18} />
              <span>Reports</span>
            </button>
          ) : null}

          {canViewActivityLogs ? (
            <button
              type="button"
              className={linkClass(isActivityLogs)}
              onClick={() => go("/admin/dashboard/activity-logs")}
            >
              <History size={18} />
              <span>Activity Logs</span>
            </button>
          ) : null}

          {canViewPosters ? (
            <button
              type="button"
              className={linkClass(isPosters)}
              onClick={() => go("/admin/dashboard/posters")}
            >
              <ImagePlus size={18} />
              <span>Posters</span>
            </button>
          ) : null}

          {canViewImageManager ? (
            <button
              type="button"
              className={linkClass(isImageManager)}
              onClick={() => go("/admin/dashboard/image-manager")}
            >
              <Images size={18} />
              <span>Image Manager</span>
            </button>
          ) : null}

          {canViewShortVideos ? (
            <button
              type="button"
              className={linkClass(isShortVideos)}
              onClick={() => go("/admin/dashboard/short-videos")}
            >
              <Video size={18} />
              <span>Short Videos</span>
            </button>
          ) : null}

          {canViewBroadcastMessages ? (
            <button
              type="button"
              className={linkClass(isBroadcastMessages)}
              onClick={() => go("/admin/dashboard/broadcast-messages")}
            >
              <Megaphone size={18} />
              <span>Broadcast Message</span>
            </button>
          ) : null}

          {canViewCoupons ? (
            <button
              type="button"
              className={linkClass(isCoupons)}
              onClick={() => go("/admin/dashboard/coupons")}
            >
              <Tag size={18} />
              <span>Coupons</span>
            </button>
          ) : null}

          {canViewUsers ? (
  <button
    type="button"
    className={linkClass(isUserNotifications)}
    onClick={() => go("/admin/dashboard/user-notifications")}
  >
    <BellRing size={18} />
    <span>User Notifications</span>
  </button>
) : null}

          {canViewSettings ? (
            <button
              type="button"
              className={linkClass(isSettings)}
              onClick={() => go("/admin/dashboard/settings")}
            >
              <Settings size={18} />
              <span>Settings</span>
            </button>
          ) : null}

          {canViewEmailTemplates ? (
            <button
              type="button"
              className={linkClass(isEmailTemplates)}
              onClick={() => go("/admin/dashboard/email-templates")}
            >
              <Mail size={18} />
              <span>Email Templates</span>
            </button>
          ) : null}
        </nav>
      </div>

      <div className={`z-20 shrink-0 border-t border-rose-100/70 bg-white/95 shadow-[0_-10px_24px_rgba(15,23,42,0.035)] backdrop-blur-xl ${collapsed ? "px-2 pb-3 pt-2" : "px-3 pb-3 pt-2"}`}>
        <div className="relative">
          <div
            className={`absolute bottom-[calc(100%+8px)] left-0 right-0 transition-all duration-300 ease-out ${
              actionsOpen
                ? "pointer-events-auto translate-y-0 opacity-100"
                : "pointer-events-none translate-y-4 opacity-0"
            } ${collapsed ? "left-0 right-auto" : ""}`}
          >
            <div className={`${collapsed ? "w-[210px]" : "w-full"} space-y-2 rounded-[18px] border border-rose-100 bg-white/95 p-2.5 shadow-[0_22px_55px_rgba(15,23,42,0.16)] backdrop-blur-xl`}>
              {canSwitchDashboard ? (
                <button
                  type="button"
                  onClick={() => {
                    setActionsOpen(false);
                    setSwitchModalOpen(true);
                  }}
                  className="flex w-full items-center justify-center gap-3 rounded-[12px] bg-[#d86a78] px-5 py-3 text-[15px] font-semibold text-white transition hover:bg-[#c95b69]"
                >
                  <Repeat size={18} />
                  <span>Switch Panel</span>
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => {
                  setActionsOpen(false);
                  onLogout?.();
                }}
                className="flex w-full items-center justify-center gap-3 rounded-[12px] border border-[#f2c6cc] bg-white px-5 py-3 text-[15px] font-semibold text-slate-400 transition hover:bg-slate-50 hover:text-[#d86a78]"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setActionsOpen((current) => !current)}
            className={`group/actions relative flex w-full overflow-hidden rounded-[14px] border text-[12px] font-semibold transition-all focus:outline-none focus:ring-4 focus:ring-rose-100 ${collapsed ? "h-11 items-center justify-center px-0" : "min-h-[118px] items-start px-3 py-3 text-left"} ${
              actionsOpen
                ? "border-[#e96578] bg-[linear-gradient(135deg,#fff0f3,#ffe4ea)] text-[#d94f66] shadow-[0_10px_24px_rgba(216,106,120,0.15)]"
                : "border-[#f2c6cc] bg-[linear-gradient(145deg,#fff5f7_0%,#ffe8ed_100%)] text-[#d86a78] hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(216,106,120,0.13)]"
            }`}
            aria-label={actionsOpen ? "Close sidebar actions" : "Open sidebar actions"}
            aria-expanded={actionsOpen}
          >
            {collapsed ? (
              <Zap size={18} />
            ) : (
              <>
                <span className="relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-white/80 text-[#ff4e70] shadow-sm"><Zap size={17} fill="currentColor" /></span>
                <span className="relative z-10 ml-2 min-w-0 flex-1">
                  <span className="block font-black text-[15px] text-[#df4e68]">Actions</span>
                  <span className="mt-0.5 block text-[8px] font-medium text-slate-400">Quick tools to manage your store</span>
                </span>
                <ChevronUp size={15} className={`relative z-10 mt-1 shrink-0 transition-transform duration-300 ${actionsOpen ? "rotate-180" : ""}`} />
                <img src={dashboardGroceryBag} alt="" aria-hidden="true" className="pointer-events-none absolute left-4 top-[52px] w-[82px] max-w-none opacity-95 drop-shadow-[0_8px_12px_rgba(84,48,46,0.10)] transition-transform duration-500 group-hover/actions:scale-105" />
                <span className="absolute bottom-3 right-3 rotate-[-8deg] text-right text-[9px] font-semibold italic leading-tight text-[#e75970]">Save Time<br />Do More</span>
              </>
            )}
          </button>
        </div>

        <DashboardSwitchModal
          open={switchModalOpen}
          onClose={() => setSwitchModalOpen(false)}
          onSwitched={handleSwitched}
        />
      </div>
    </div>
  );
}

export function MobileSidebar({ open, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const onLogout = () => {
    clearAuthFlow();
    clearAuthSession();
    navigate("/admin/login", { replace: true });
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 xl:hidden">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close menu"
        onClick={onClose}
      />

      <aside className="absolute left-0 top-0 flex h-full w-[280px] max-w-[86vw] flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between px-4 pt-4">
          <div className="text-[14px] font-semibold text-slate-500">Menu</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-2 h-px w-full bg-slate-200/70" />

        <div className="min-h-0 flex-1">
          <SidebarContent onNavigate={onClose} onLogout={onLogout} collapsed={false} />
        </div>
      </aside>
    </div>
  );
}

export default function Sidebar({ collapsed = false, onToggleCollapse = null }) {
  const navigate = useNavigate();

  const onLogout = () => {
    clearAuthFlow();
    clearAuthSession();
    navigate("/admin/login", { replace: true });
  };

  return (
    <aside
      className={`fixed left-0 top-0 z-40 hidden h-screen shrink-0 border-r border-slate-200 bg-white shadow-[4px_0_24px_rgba(15,23,42,0.025)] transition-[width] duration-300 xl:block ${collapsed ? "w-[78px]" : "w-[232px]"}`}
    >
      <SidebarContent
        onLogout={onLogout}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        showCollapseControl
      />
    </aside>
  );
}
